/* eslint-env es6 */
import styles from './newRewardSchemes.module.scss';
import backIcon from 'images/back.png';
import {DatePicker, Form, Input, Select, Spin, Switch, Tooltip} from 'antd';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {
  operationTypeEnum,
  referralRewardType,
  rewardAssetClass,
  rewardSchemeType,
  rewardTriggerEnum,
  rewardType,
  walletCurrencies,
} from 'utils/mock';
import TextArea from 'antd/lib/input/TextArea';
import {useNavigate, useParams} from 'react-router-dom';
import {useQuery} from '@apollo/client';
import {GET_ALL_STOCK_REWARDS} from 'graphql/queries/rewardScheme';
import {fieldRules} from 'utils/helper';
import {useRewardSchemeForm} from './useRewardSchemeForm';
import {AllowedCurrenciesSection} from '../entities/AllowedCurrenciesSection';
import {CashRewardConfig} from '../entities/CashRewardConfig';
import {FeeWaiverConfiguration} from '../entities/FeeWaiverConfiguration';
import {InstrumentKeysField} from '../entities/InstrumentKeysField';
import {LeaderboardConfig} from '../entities/LeaderboardConfig';
import {RewardInstructions} from '../entities/RewardInstructions';
import {StocksRewardConfig} from '../entities/StocksRewardConfig';
import deleteIcon from 'images/delete.svg';
import plusIcon from 'images/plus-circle.png';
import moment from 'moment';

const NewRewardScheme = () => {
  const [form] = Form.useForm();
  const {id, language} = useParams();
  const {
    type,
    rewardTrigger,
    operationType,
    randomStock,
    shouldShowMinValue,
    hasStocksReward,
    legacyTicker,
    hasCashReward,
    hasFeeWaiverReward,
    handleSave,
    loading,
  } = useRewardSchemeForm(form, {rewardSchemeId: id, language});

  // The gateway pages this at 10 by default, so the instrument picker only
  // ever saw the ten oldest banks: a newly created one could not be chosen,
  // and a scheme already pointing at one rendered blank because its option was
  // not in the list. This is a picker, not a paged table — fetch the lot.
  const {loading: stocksLoading, data} = useQuery(GET_ALL_STOCK_REWARDS, {
    variables: {page: 1, limit: 200},
  });

  const navigate = useNavigate();
  const {Option} = Select;

  const disabledDate = current => {
    return current && current < moment().startOf('day');
  };
  const validateDuration = () => {
    const durationUnit = form.getFieldValue('cumulativeDurationUnit');
    const durationValue = form.getFieldValue('cumulativeDurationValue');
    let max;

    // Determine the max value based on the selected duration unit
    switch (durationUnit) {
      case 'DAYS':
        max = 31;
        break;
      case 'WEEKS':
        max = 4;
        break;
      case 'MONTHS':
        max = 1;
        break;
      default:
        max = 31;
    }

    // Check mutual selection requirement
    if ((durationUnit && !durationValue) || (!durationUnit && durationValue)) {
      return Promise.reject(
        new Error('Both Duration fields must be selected together.'),
      );
    }

    if (durationValue <= 0 && durationValue) {
      return Promise.reject(
        new Error(
          'Invalid Duration value. Value must be within range for the selected Duration.',
        ),
      );
    }

    // Check maximum value condition
    if (durationValue > max) {
      return Promise.reject(
        new Error(
          `Value must be less than or equal to ${max} for the selected Duration`,
        ),
      );
    }

    return Promise.resolve();
  };

  const handleUnitChange = () => {
    form.validateFields(['cumulativeDurationValue']);
  };

  const handleValueChange = () => {
    form.validateFields(['cumulativeDurationUnit']);
  };

  const pageTitle = id ? 'Edit Reward Scheme' : 'New Reward Scheme';
  const startDateRules = id ? [] : [fieldRules('start  date is required')];

  if (id && loading) {
    return (
      <div className="query-loading">
        <Spin size="large" />
        <h1>Loading...</h1>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.navigation}>
        <img src={backIcon} alt="Go back" onClick={() => navigate(-1)} />
        <span>
          <span className={styles['h-style']}>{pageTitle}</span>
        </span>
      </div>
      <div className={styles.form}>
        <Form
          form={form}
          onFinish={handleSave}
          layout="vertical"
          initialValues={{
            referee: 'NONE',
            referer: 'NONE',
            transactional: 'NONE',
            randomStock: false,
            allowConcurrency: false,
            instructions: [{titleText: '', descriptionText: ''}],
            allowedCurrencies: [{currency: 'USD', minTransactionValue: 0}],
            maxFeeWaivers: [{currency: 'USD', value: 0}],
          }}>
          <Form.Item
            label="Reward Scheme Type"
            className={styles.field}
            name="type"
            rules={[
              {
                required: true,
                message: 'Reward scheme type is required',
              },
            ]}>
            <Select allowClear>
              {rewardSchemeType.map(({key, name}) => (
                <Option key={key} value={key}>
                  {name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            rules={[fieldRules('title is required')]}
            label="Title"
            className={styles.field}
            name="title">
            <Input name="title" placeholder="Reward scheme title" />
          </Form.Item>
          <Form.Item
            rules={[fieldRules('description is required')]}
            label="Description"
            className={styles.field}
            name="description">
            <TextArea
              name="description"
              placeholder="Reward scheme description..."
              rows={4}
            />
          </Form.Item>
          {type === 'REFERRAL' && (
            <div className={styles['date-format']}>
              <Form.Item
                label="Referee Reward"
                className={styles.field}
                name="referee"
                rules={[fieldRules('referee is required')]}>
                <Select allowClear>
                  {referralRewardType?.map(({key, name}) => (
                    <Option key={key} value={key}>
                      {name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Referer Reward"
                className={styles.field}
                name="referer"
                rules={[fieldRules('referer is required')]}>
                <Select allowClear>
                  {referralRewardType?.map(({key, name}) => (
                    <Option key={key} value={key}>
                      {name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          )}
          {type === 'TRANSACTIONAL' && (
            <Form.Item
              label="Transactional Reward"
              className={styles.field}
              name="transactional"
              rules={[fieldRules('transactional is required')]}>
              <Select allowClear name="transactional">
                {rewardType.map(({key, name}) => (
                  <Option value={key} key={key}>
                    {name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <div>
            <h1 className={styles['top-header-text']}>
              Operation Configuration
            </h1>
          </div>
          <div className={styles['date-format']}>
            <Form.Item
              label="Operation Type"
              name="operationType"
              rules={[fieldRules('operation type is required')]}>
              <Select allowClear name="operationType">
                {operationTypeEnum.map(({key, name}) => (
                  <Option key={key} value={key}>
                    {name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Reward Trigger"
              name="rewardTrigger"
              rules={[fieldRules('reward trigger is required')]}>
              <Select allowClear name="rewardTrigger">
                {rewardTriggerEnum.map(({key, name}) => (
                  <Option key={key} value={key}>
                    {name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          {type === 'WHEEL_SPIN' && (
            <div className={styles['segment-wrapper']}>
              <Form.Item
                label="Max Number of Wheel Spins"
                name="maxNumberOfSpins"
                tooltip="Maximum **total number of spin attempts** allowed per user for this reward scheme. This includes all spins (both wins and losses) and does NOT reset. Once a user reaches this limit, they cannot spin again, regardless of how many times they've won."
                className={styles.field}
                rules={[fieldRules('max number of wheel spins is required')]}>
                <Input type="number" min={1} name="maxNumberOfSpins" />
              </Form.Item>
              <Form.Item
                label={
                  <Tooltip
                    title={
                      <div className={styles.tooltipContent}>
                        <p>
                          <strong>(Optional):</strong> How long a won reward
                          remains valid before expiring:
                        </p>
                        <ul>
                          <li>
                            Set the value and unit (DAYS, WEEKS, or MONTHS)
                          </li>
                          <li>If not set, rewards don&apos;t expire</li>
                          <li>
                            Users must complete a qualifying transaction
                            (deposit/investment) within this period to claim the
                            reward
                          </li>
                        </ul>
                      </div>
                    }>
                    Expiration Duration Unit
                  </Tooltip>
                }
                name="expirationDurationUnit">
                <Select
                  allowClear
                  name="expirationDurationUnit"
                  className={styles.field}>
                  <Option key="DAYS">Days</Option>
                  <Option key="WEEKS">Weeks</Option>
                  <Option key="MONTHS">Months</Option>
                </Select>
              </Form.Item>
              <Form.Item
                label="Expiration Duration Value"
                className={styles.field}
                name="expirationDurationValue">
                <Input type="number" min={1} name="expirationDurationValue" />
              </Form.Item>

              <div className={styles.segmentsContainer}>
                <h2 className={styles.title}>Segments</h2>
                <Form.List name="segments">
                  {(fields, {add, remove}) => (
                    <>
                      <div className={styles.segmentsList}>
                        {fields.map(({key, name: fieldName}) => (
                          <div key={key} className={styles.rewardItem}>
                            {fields.length > 1 && (
                              <img
                                src={deleteIcon}
                                alt="Delete"
                                className={styles.deleteIcon}
                                onClick={e => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  remove(fieldName);
                                }}
                              />
                            )}
                            <div className={styles.rewardFields}>
                              <Form.Item
                                label="Reward Segment"
                                name={[fieldName, 'reward']}
                                rules={[fieldRules('reward is required')]}>
                                <Select allowClear>
                                  {rewardType.map(({key: rewardKey, name}) => (
                                    <Option value={rewardKey} key={rewardKey}>
                                      {name}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                              <Form.Item
                                label="Reward Weight"
                                tooltip="Probability weight (higher = more likely). Weights are relative - a segment with weight 10 is twice as likely as one with weight 5."
                                name={[fieldName, 'weight']}
                                rules={[
                                  fieldRules('reward weight is required'),
                                ]}>
                                <Input type="number" min={1} />
                              </Form.Item>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div onClick={add} className={styles['add-button']}>
                        <img src={plusIcon} alt="Add" />
                        <h1>Add Reward Segment</h1>
                      </div>
                    </>
                  )}
                </Form.List>
              </div>
            </div>
          )}
          {type === 'REFERRAL' && rewardTrigger === 'FIRST' && (
            <div className={styles['duration']}>
              <Form.Item
                name="cumulativeDurationUnit"
                className={styles.field}
                rules={[{validator: validateDuration}]}
                label={
                  <Tooltip title="This defines the duration for tracking transactions and deposits, starting from the time the referred user signs up. For example, if you select 'Days' and enter a value of 4, it means we will track the user's activity starting 4 days after their signup.">
                    Cumulative Duration
                  </Tooltip>
                }>
                <Select onChange={handleUnitChange} allowClear>
                  <Option key="DAYS">Days</Option>
                  <Option key="WEEKS">Weeks</Option>
                  <Option key="MONTHS">Months</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="cumulativeDurationValue"
                className={styles.field}
                rules={[{validator: validateDuration}]}
                label={
                  <Tooltip
                    title={
                      <div>
                        <p>
                          Enter a value for the cumulative duration. The maximum
                          value depends on the selected duration unit:
                        </p>
                        <ul>
                          <li>Daily: up to 31</li>
                          <li>Weeks: up to 4</li>
                          <li>Months: up to 1</li>
                        </ul>
                        <p>
                          Ensure that both the duration unit and value are
                          selected together.
                        </p>
                      </div>
                    }>
                    Cumulative Duration Value
                  </Tooltip>
                }>
                <Input
                  type="number"
                  onChange={e => handleValueChange(e.target.value)}
                />
              </Form.Item>
            </div>
          )}
          {operationType === 'INVESTMENT' && (
            <>
              <Form.Item
                label="Asset Class"
                className={styles.field}
                name="assetClass"
                rules={[
                  // Asset class and instrument type are independent scopes,
                  // and either alone is a valid one — but a scheme that names
                  // neither describes nothing, and the gate fails every event
                  // against it. The rule lives on this field because it is the
                  // first of the pair; `dependencies` clears it as soon as the
                  // other one is filled.
                  ({getFieldValue}) => ({
                    validator: (_, value) =>
                      value?.length || getFieldValue('instrumentKeys')?.length
                        ? // eslint-disable-next-line no-undef
                          Promise.resolve()
                        : // eslint-disable-next-line no-undef
                          Promise.reject(
                            new Error(
                              'Select an asset class or an instrument type',
                            ),
                          ),
                  }),
                ]}
                dependencies={['instrumentKeys']}>
                <Select mode="multiple" name="assetClass">
                  {rewardAssetClass.map(({key, name}) => (
                    <Option value={key} key={key}>
                      {name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <InstrumentKeysField />
            </>
          )}

          <StocksRewardConfig
            hasReward={hasStocksReward}
            randomStock={randomStock}
            loading={stocksLoading}
            stockOptions={data?.getAllStockBanks?.data?.stockRewardBank || []}
            legacyTicker={legacyTicker}
          />
          <CashRewardConfig
            hasReward={hasCashReward}
            walletCurrencies={walletCurrencies}
          />
          <FeeWaiverConfiguration
            form={form}
            hasReward={hasFeeWaiverReward}
            walletCurrencies={walletCurrencies}
          />
          <LeaderboardConfig
            form={form}
            visible={type === 'LEADERBOARD'}
            stockOptions={data?.getAllStockBanks?.data?.stockRewardBank || []}
          />
          <RewardInstructions form={form} />

          <div className={styles['date-format']}>
            {shouldShowMinValue && (
              <Form.Item
                label={`Max Number of times user can earn reward`}
                name="maxReward"
                rules={[
                  {
                    required: true,
                    message: 'max reward is required',
                  },
                ]}>
                <Input type="number" name="maxReward" min={1} placeholder="2" />
              </Form.Item>
            )}
            <Form.Item
              rules={startDateRules}
              name="startDate"
              label="Start Date">
              <DatePicker format={'YYYY-MM-DD'} disabledDate={disabledDate} />
            </Form.Item>
            <Form.Item
              rules={[fieldRules('expiry date is required')]}
              name="expirationDate"
              label="Expiration Date">
              <DatePicker format={'YYYY-MM-DD'} disabledDate={disabledDate} />
            </Form.Item>
          </div>

          <AllowedCurrenciesSection form={form} />

          <Form.Item
            label="Allow Concurrency"
            className={styles.field}
            name="allowConcurrency"
            valuePropName="checked">
            <Switch />
          </Form.Item>
          <SubmitButton label={id ? 'Update' : 'Create'} disabled={loading} />
        </Form>
      </div>
    </div>
  );
};

export {NewRewardScheme};
